import { useEffect } from "react";
import { useParams, Link, useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SEOHead } from "@/components/seo";
import { AccreditationBadges } from "@/components/agent/AccreditationBadges";
import SoldListingsGrid from "@/components/sold/SoldListingsGrid";

import { useAgentBySlug, type AgentProfile as AgentProfileType } from "@/hooks/useAgentBySlug";
import { supabase } from "@/integrations/supabase/client";
import { formatAgentRole } from "@/lib/utils";
import { trackGA4Event } from "@/lib/ga4";
import { 
  Phone, Mail, MapPin, Calendar, Star, Building, Award, ArrowLeft, Share2, Shield
} from "lucide-react";
import SocialShare from "@/components/social/SocialShare";

// Build-time data loader for SSG. Runs at build for each pre-rendered slug,
// and on client navigations. Returns agent data so SEO/content render in HTML.
export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (!slug) return { agent: null };
  const { data } = await supabase
    .from("agents_public")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  return { agent: (data as AgentProfileType | null) ?? null };
}

const AgentProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const loaderData = useLoaderData() as { agent: AgentProfileType | null } | undefined;
  const queryResult = useAgentBySlug(slug);
  const agent = loaderData?.agent ?? queryResult.data ?? null;
  const isLoading = !loaderData?.agent && queryResult.isLoading;
  const error = queryResult.error;

  useEffect(() => {
    if (agent?.display_name) {
      document.title = `${agent.display_name} | Real Estate Agent | Prime Realty Partners`;
    }
  }, [agent?.display_name]);
  

  

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (error || !agent) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Agent Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The agent profile you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/agents">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Agents
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const initials = agent.display_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "A";

  const profileUrl = `https://prpsj.com/agent/${slug}`;

  const firstName = agent.display_name?.split(" ")[0] || "";

  const agentSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": agent.display_name,
    "jobTitle": "Real Estate Agent",
    "worksFor": {
      "@type": "RealEstateBrokerage",
      "name": "Prime Realty Partners",
      "url": "https://www.prpsj.com",
    },
    "url": profileUrl,
    "telephone": agent.phone || undefined,
    "email": agent.email || undefined,
    "image": agent.photo_url || undefined,
    "areaServed": {
      "@type": "State",
      "name": "New Jersey",
    },
  };

  return (
    <Layout>
      <SEOHead
        title={`${agent.display_name} - Real Estate Agent | Prime Realty Partners`}
        description={`${agent.display_name} — Real estate agent serving South Jersey with Prime Realty Partners. Contact ${firstName} for expert help buying or selling your home.`}
        canonicalUrl={profileUrl}
        keywords={`${agent.display_name}, real estate agent, ${agent.service_areas?.join(", ") || "NJ"}, Prime Realty Partners`}
        schema={[agentSchema]}
        ogImage={agent.photo_url || "https://www.prpsj.com/og-image.png"}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-primary/5 py-12">
        <div className="container">
          <Link to="/agents" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to All Agents
          </Link>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Photo */}
            <div className="md:col-span-1">
              <div className="aspect-square rounded-xl overflow-hidden bg-muted shadow-xl">
                {agent.photo_url ? (
                  <img
                    src={agent.photo_url}
                    alt={`${agent.display_name || "Agent"} — Real Estate Agent at Prime Realty Partners`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-8xl font-bold text-muted-foreground/30">
                    {initials}
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-2 space-y-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-4xl md:text-5xl font-bold">
                    {agent.display_name}
                  </h1>
                  {agent.agent_role && (
                    <Badge className="bg-primary text-primary-foreground">
                      {formatAgentRole(agent.agent_role)}
                    </Badge>
                  )}
                </div>
                {agent.headline && (
                  <p className="text-xl text-primary font-medium">{agent.headline}</p>
                )}
                {agent.licensed_states && agent.licensed_states.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Shield className="h-4 w-4" />
                    <span>Licensed in: {agent.licensed_states.join(", ")}</span>
                  </div>
                )}
              </div>

              {/* Credentials */}
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                {agent.years_experience && agent.years_experience > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 text-primary" />
                    <span>{agent.years_experience} Years Experience</span>
                  </div>
                )}
                {agent.licensed_year && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-5 w-5 text-primary" />
                    <span>Licensed Since {agent.licensed_year}</span>
                  </div>
                )}
                {agent.license_number && (
                  <div className="flex items-center gap-1">
                    <Award className="h-5 w-5 text-primary" />
                    <span>License: {agent.license_number}</span>
                  </div>
                )}
              </div>

              {/* Accreditations */}
              {agent.accreditations && agent.accreditations.length > 0 && (
                <div>
                  <AccreditationBadges accreditations={agent.accreditations} maxDisplay={8} size="md" />
                </div>
              )}

              {/* Contact Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                {agent.phone && (
                  <Button size="lg" asChild>
                    <a href={`tel:${agent.phone}`} onClick={() => trackGA4Event('phone_click', { event_category: 'lead_action', agent_name: agent.display_name, agent_slug: slug })}>
                      <Phone className="h-5 w-5 mr-2" />
                      {agent.phone}
                    </a>
                  </Button>
                )}
                {agent.email && (
                  <Button size="lg" variant="outline" asChild>
                    <a href={`mailto:${agent.email}`} onClick={() => trackGA4Event('email_click', { event_category: 'lead_action', agent_name: agent.display_name, agent_slug: slug })}>
                      <Mail className="h-5 w-5 mr-2" />
                      Email Me
                    </a>
                  </Button>
                )}
                <Button size="lg" variant="ghost">
                  <Share2 className="h-5 w-5 mr-2" />
                  Share Profile
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Bio & Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              {agent.bio && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      About {agent.display_name?.split(" ")[0]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {agent.bio}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Recent Sales */}
              {agent.mls_id && (
                <SoldListingsGrid
                  agentId={agent.mls_id}
                  showFilters={false}
                  showPagination={true}
                  title="Recent Sales"
                />
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Service Areas */}
              {agent.service_areas && agent.service_areas.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MapPin className="h-5 w-5 text-primary" />
                      Service Areas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {agent.service_areas.map((area, index) => (
                        <Badge key={index} variant="secondary">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Specialties */}
              {agent.specialties && agent.specialties.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Star className="h-5 w-5 text-primary" />
                      Specialties
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {agent.specialties.map((specialty, index) => (
                        <Badge key={index} variant="outline" className="border-primary/30">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contact Card */}
              <Card className="bg-primary text-primary-foreground">
                <CardHeader>
                  <CardTitle className="text-lg">Get in Touch</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm opacity-90">
                    Ready to buy or sell? Contact {agent.display_name?.split(" ")[0]} today!
                  </p>
                  {agent.phone && (
                    <Button variant="secondary" className="w-full" asChild>
                      <a href={`tel:${agent.phone}`} onClick={() => trackGA4Event('phone_click', { event_category: 'lead_action', agent_name: agent.display_name, agent_slug: slug })}>
                        <Phone className="h-4 w-4 mr-2" />
                        {agent.phone}
                      </a>
                    </Button>
                  )}
                  {agent.email && (
                    <Button variant="outline" className="w-full bg-transparent border-primary-foreground/30 hover:bg-primary-foreground/10" asChild>
                      <a href={`mailto:${agent.email}`} onClick={() => trackGA4Event('email_click', { event_category: 'lead_action', agent_name: agent.display_name, agent_slug: slug })}>
                        <Mail className="h-4 w-4 mr-2" />
                        {agent.email}
                      </a>
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Share */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Share This Profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <SocialShare
                    url={profileUrl}
                    title={`${agent.display_name} - Real Estate Agent`}
                    description={agent.headline || `Real Estate Agent at Prime Realty Partners`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AgentProfile;
